import { Language } from './translations';
import { agentSlugs, getAgentIdBySlug } from './agentSlugs';
import {
  competenceCityPath,
  competenceStatePath,
  findCompetenceCityBySlugs,
  findCompetenceStateBySlug,
} from './competenceCenterIndex';

// Route mappings for both languages
export const routes = {
  home: {
    de: '/de',
    en: '/en',
  },
  functionalities: {
    de: '/de/plattform',
    en: '/en/platform',
  },
  pricing: {
    de: '/de/preise',
    en: '/en/pricing',
  },
  successStories: {
    de: '/de/success-stories',
    en: '/en/success-stories',
  },
  successStoryDetail: {
    de: '/de/success-story/:companyName',
    en: '/en/success-story/:companyName',
  },
  partners: {
    de: '/de/partner',
    en: '/en/partners',
  },
  team: {
    de: '/de/team',
    en: '/en/team',
  },
  events: {
    de: '/de/veranstaltungen',
    en: '/en/events',
  },
  careers: {
    de: '/de/karriere',
    en: '/en/careers',
  },
  careerDetail: {
    de: '/de/karriere/:jobId',
    en: '/en/careers/:jobId',
  },
  definitions: {
    de: '/de/definitionen',
    en: '/en/definitions',
  },
  definitionDetail: {
    de: '/de/definitionen/:slug',
    en: '/en/definitions/:slug',
  },
  battleCards: {
    de: '/de/battle-cards',
    en: '/en/battle-cards',
  },
  faq: {
    de: '/de/faq',
    en: '/en/faq',
  },
  costOfInaction: {
    de: '/de/cost-of-inaction',
    en: '/en/cost-of-inaction',
  },
  smartDataForge: {
    de: '/de/smart-data-forge',
    en: '/en/smart-data-forge',
  },
  expertTalk: {
    de: '/de/vier-experten-fuenf-meinungen',
    en: '/en/four-experts-five-opinions',
  },
  processes: {
    de: '/de/prozesse',
    en: '/en/processes',
  },
  endToEndProcess: {
    de: '/de/prozesse/:processSlug',
    en: '/en/processes/:processSlug',
  },
  battleCardDetail: {
    de: '/de/battle-cards/:slug',
    en: '/en/battle-cards/:slug',
  },
  topTenList: {
    de: '/de/battle-cards/top-10/:slug',
    en: '/en/battle-cards/top-10/:slug',
  },
  downloads: {
    de: '/de/downloads',
    en: '/en/downloads',
  },
  downloadThankYou: {
    de: '/de/download-vielen-dank',
    en: '/en/download-thank-you',
  },
  useCases: {
    de: '/de/use-cases/:useCaseName',
    en: '/en/use-cases/:useCaseName',
  },
  contact: {
    de: '/de/kontakt',
    en: '/en/contact',
  },
  imprint: {
    de: '/de/impressum',
    en: '/en/imprint',
  },
  privacy: {
    de: '/de/datenschutz',
    en: '/en/privacy',
  },
  terms: {
    de: '/de/nutzungsbedingungen',
    en: '/en/terms',
  },
  aiAgents: {
    de: '/de/frontier-agents',
    en: '/en/frontier-agents',
  },
  minerva: {
    de: '/de/minerva',
    en: '/en/minerva',
  },
  agentLibrary: {
    de: '/de/minerva/agenten',
    en: '/en/minerva/agents',
  },
  agentDetail: {
    de: '/de/minerva/agenten/:agentSlug',
    en: '/en/minerva/agents/:agentSlug',
  },
  competenceCenter: {
    de: '/de/kompetenzcenter',
    en: '/en/competence-center',
  },
  competenceCenterState: {
    de: '/de/kompetenzcenter/:regionSlug',
    en: '/en/competence-center/:regionSlug',
  },
  competenceCenterCity: {
    de: '/de/kompetenzcenter/:regionSlug/:citySlug',
    en: '/en/competence-center/:regionSlug/:citySlug',
  },
  maintenance: {
    de: '/de/wartung',
    en: '/en/maintenance',
  },
} as const;

// Reverse mapping: from path to route key
const pathToRouteKey: Record<string, keyof typeof routes> = {
  '/de': 'home',
  '/en': 'home',
  '/de/plattform': 'functionalities',
  '/en/platform': 'functionalities',
  '/de/preise': 'pricing',
  '/en/pricing': 'pricing',
  '/de/success-stories': 'successStories',
  '/en/success-stories': 'successStories',
  '/de/partner': 'partners',
  '/en/partners': 'partners',
  '/de/team': 'team',
  '/en/team': 'team',
  '/de/veranstaltungen': 'events',
  '/en/events': 'events',
  '/de/karriere': 'careers',
  '/en/careers': 'careers',
  '/de/definitionen': 'definitions',
  '/en/definitions': 'definitions',
  '/de/battle-cards': 'battleCards',
  '/en/battle-cards': 'battleCards',
  '/de/faq': 'faq',
  '/en/faq': 'faq',
  '/de/cost-of-inaction': 'costOfInaction',
  '/en/cost-of-inaction': 'costOfInaction',
  '/de/smart-data-forge': 'smartDataForge',
  '/en/smart-data-forge': 'smartDataForge',
  '/de/vier-experten-fuenf-meinungen': 'expertTalk',
  '/en/four-experts-five-opinions': 'expertTalk',
  '/de/downloads': 'downloads',
  '/en/downloads': 'downloads',
  '/de/kontakt': 'contact',
  '/en/contact': 'contact',
  '/de/impressum': 'imprint',
  '/en/imprint': 'imprint',
  '/de/datenschutz': 'privacy',
  '/en/privacy': 'privacy',
  '/de/nutzungsbedingungen': 'terms',
  '/en/terms': 'terms',
  '/de/download-vielen-dank': 'downloadThankYou',
  '/en/download-thank-you': 'downloadThankYou',
  '/de/frontier-agents': 'aiAgents',
  '/en/frontier-agents': 'aiAgents',
  '/de/minerva': 'minerva',
  '/en/minerva': 'minerva',
  '/de/prozesse': 'processes',
  '/en/processes': 'processes',
  '/de/minerva/agenten': 'agentLibrary',
  '/en/minerva/agents': 'agentLibrary',
  '/de/kompetenzcenter': 'competenceCenter',
  '/en/competence-center': 'competenceCenter',
  '/en/maintenance': 'maintenance',
  '/de/wartung': 'maintenance',
};

/**
 * Extract language from URL pathname
 */
export function getLanguageFromPath(pathname: string): Language {
  if (pathname.startsWith('/de')) {
    return 'de';
  }
  if (pathname.startsWith('/en')) {
    return 'en';
  }
  // Default to German for root and unknown paths
  return 'de';
}

/**
 * Check if pathname has a language prefix
 */
export function isLanguageRoute(pathname: string): boolean {
  return pathname.startsWith('/de/') || pathname.startsWith('/en/') || pathname === '/de' || pathname === '/en';
}

/**
 * Get the route path for a given route key and language
 */
export function getRoutePath(routeKey: keyof typeof routes, lang: Language, params?: Record<string, string>): string {
  const route = routes[routeKey][lang];
  
  if (!params) {
    return route;
  }
  
  // Replace dynamic parameters
  let path: string = route;
  for (const [key, value] of Object.entries(params)) {
    path = path.replace(`:${key}`, value);
  }
  
  return path;
}

/**
 * Translate current route to target language
 * Preserves dynamic parameters (companyName, category, useCaseName)
 */
export function translateRoute(pathname: string, targetLang: Language): string {
  // Handle root paths
  if (pathname === '/' || pathname === '/de' || pathname === '/en') {
    return routes.home[targetLang];
  }
  
  // Handle maintenance (same for both)
  if (pathname === '/maintenance') {
    return '/maintenance';
  }
  
  // Extract current language
  const currentLang = getLanguageFromPath(pathname);
  
  // If already in target language, return as-is
  if (currentLang === targetLang && isLanguageRoute(pathname)) {
    return pathname;
  }
  
  // Handle dynamic routes
  // Success story detail: /de/success-story/:companyName or /en/success-story/:companyName
  const successStoryMatch = pathname.match(/^\/(?:de|en)\/success-story\/(.+)$/);
  if (successStoryMatch) {
    return getRoutePath('successStoryDetail', targetLang, { companyName: successStoryMatch[1] });
  }
  
  // Use cases: /de/use-cases/:useCaseName or /en/use-cases/:useCaseName
  const useCaseMatch = pathname.match(/^\/(?:de|en)\/use-cases\/(.+)$/);
  if (useCaseMatch) {
    return getRoutePath('useCases', targetLang, { useCaseName: useCaseMatch[1] });
  }

  // Career detail: /de/karriere/:jobId or /en/careers/:jobId
  const careerDetailMatch = pathname.match(/^\/(?:de|en)\/(?:karriere|careers)\/(.+)$/);
  if (careerDetailMatch) {
    return getRoutePath('careerDetail', targetLang, { jobId: careerDetailMatch[1] });
  }

  // Definition detail: /de/definitionen/:slug or /en/definitions/:slug
  const definitionDetailMatch = pathname.match(/^\/(?:de|en)\/(?:definitionen|definitions)\/(.+)$/);
  if (definitionDetailMatch) {
    return getRoutePath('definitionDetail', targetLang, { slug: definitionDetailMatch[1] });
  }

  // Top-10 list: /de/battle-cards/top-10/:slug — checked before the battle
  // card detail, whose pattern would otherwise swallow it.
  const topTenListMatch = pathname.match(/^\/(?:de|en)\/battle-cards\/top-10\/(.+)$/);
  if (topTenListMatch) {
    return getRoutePath('topTenList', targetLang, { slug: topTenListMatch[1] });
  }

  // Battle card detail: /de/battle-cards/:slug or /en/battle-cards/:slug
  const battleCardDetailMatch = pathname.match(/^\/(?:de|en)\/battle-cards\/(.+)$/);
  if (battleCardDetailMatch) {
    return getRoutePath('battleCardDetail', targetLang, { slug: battleCardDetailMatch[1] });
  }

  // End-to-end process: /de/prozesse/:processSlug or /en/processes/:processSlug
  const endToEndProcessMatch = pathname.match(/^\/(?:de|en)\/(?:prozesse|processes)\/(.+)$/);
  if (endToEndProcessMatch) {
    return getRoutePath('endToEndProcess', targetLang, { processSlug: endToEndProcessMatch[1] });
  }
  
  // Agent detail: /de/minerva/agenten/:agentSlug or /en/minerva/agents/:agentSlug.
  // Unlike the other detail pages the slug differs per language (it carries
  // the agent name), so it is translated rather than kept.
  const agentDetailMatch = pathname.match(/^\/(?:de|en)\/minerva\/(?:agenten|agents)\/(.+)$/);
  if (agentDetailMatch) {
    const agentId = getAgentIdBySlug(agentDetailMatch[1]);
    return agentId
      ? getRoutePath('agentDetail', targetLang, { agentSlug: agentSlugs[agentId][targetLang] })
      : routes.agentLibrary[targetLang];
  }

  // Kompetenzcenter state and city pages: /de/kompetenzcenter/bayern/muenchen.
  // Like the agents, the slugs differ per language (bayern / bavaria), so they
  // are looked up and translated; an unknown slug falls back to the hub.
  const competenceMatch = pathname.match(/^\/(?:de|en)\/(?:kompetenzcenter|competence-center)\/([^/]+)(?:\/([^/]+))?$/);
  if (competenceMatch) {
    const [, stateSlug, citySlug] = competenceMatch;
    if (citySlug) {
      const city = findCompetenceCityBySlugs(stateSlug, citySlug);
      if (city) return competenceCityPath(city.id, targetLang);
    } else {
      const state = findCompetenceStateBySlug(stateSlug);
      if (state) return competenceStatePath(state.id, targetLang);
    }
    return routes.competenceCenter[targetLang];
  }

  // Try to find matching route key from the full pathname
  const routeKey = pathToRouteKey[pathname];
  
  if (routeKey) {
    return routes[routeKey][targetLang];
  }
  
  // Fallback: if we can't translate, return home in target language
  return routes.home[targetLang];
}

/**
 * Get route key from pathname (for internal use)
 */
export function getRouteKeyFromPath(pathname: string): keyof typeof routes | null {
  // Handle root paths
  if (pathname === '/' || pathname === '/de' || pathname === '/en') {
    return 'home';
  }
  
  // Handle maintenance
  if (pathname === '/maintenance') {
    return 'maintenance';
  }

  // Check for dynamic routes first
  if (pathname.match(/^\/(?:de|en)\/success-story\//)) {
    return 'successStoryDetail';
  }
  if (pathname.match(/^\/(?:de|en)\/use-cases\//)) {
    return 'useCases';
  }
  if (pathname.match(/^\/(?:de|en)\/(?:prozesse|processes)\/.+/)) {
    return 'endToEndProcess';
  }
  if (pathname.match(/^\/(?:de|en)\/(?:karriere|careers)\/.+/)) {
    return 'careerDetail';
  }
  if (pathname.match(/^\/(?:de|en)\/(?:definitionen|definitions)\/.+/)) {
    return 'definitionDetail';
  }
  if (pathname.match(/^\/(?:de|en)\/battle-cards\/top-10\/.+/)) {
    return 'topTenList';
  }
  if (pathname.match(/^\/(?:de|en)\/battle-cards\/.+/)) {
    return 'battleCardDetail';
  }
  if (pathname.match(/^\/(?:de|en)\/minerva\/(?:agenten|agents)\/.+/)) {
    return 'agentDetail';
  }
  if (pathname.match(/^\/(?:de|en)\/(?:kompetenzcenter|competence-center)\/[^/]+\/[^/]+$/)) {
    return 'competenceCenterCity';
  }
  if (pathname.match(/^\/(?:de|en)\/(?:kompetenzcenter|competence-center)\/[^/]+$/)) {
    return 'competenceCenterState';
  }

  // pathToRouteKey is keyed by the real path in BOTH languages, so the path
  // is looked up as-is. It used to be rewritten to its German form first
  // ("/en/pricing" -> "/de/pricing"), which only matched when the two slugs
  // happened to be identical; every English page with its own slug fell
  // through to null and rendered the generic title and meta description.
  return pathToRouteKey[pathname] ?? null;
}
